import { Injectable, Injector } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { concatMap, take, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import jspreadsheet from 'jspreadsheet';

import {
  CacheService,
  CurrentUserDataService,
  GenericHttpClientService,
  ModuleNavigationService,
  RoutingService,
} from '@shared/services';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import { ApplicationInsightsService } from './application-insights.service';
import { AppStatusService, AuthInitializerService } from './auth/services';
import { CoreAccount, CoreUser } from '@shared/models';
import { AccountType } from '@shared/enums';
import { HttpSharedCoreContext } from '@shared/enums/http-context/http-shared-core-context.enum';
import { environment } from '@env/environment';

@UntilDestroy()
@Injectable()
export class AppInitializerService {
  private readonly clientIdBlacklist = ['error', 'main'];

  constructor(
    private injector: Injector,
    private appStatusService: AppStatusService,
    private currentUserDataService: CurrentUserDataService,
    private moduleNavigationService: ModuleNavigationService,
    private appFacade: AppFacade,
    private offlineFacade: OfflineFacade,
    private authInitializerService: AuthInitializerService,
    private routingService: RoutingService,
    private cacheService: CacheService,
    private genericHttpClientService: GenericHttpClientService,
    private userDetailsFacade: UserDetailsFacade
  ) {
    jspreadsheet.setLicense(environment.jspreadsheet.key);
  }

  // TODO(Improvement) refactor this to use Observable instead of promise
  async init(): Promise<void> {
    return new Promise(async (resolve) => {
      await this.authInitializerService.init();

      await this.initializeApplicationInsights();

      const coreUser = await this.getCoreUserAccount();
      const clientId = await this.extractClientIdFromURL(coreUser);

      combineLatest([
        this.currentUserDataService.getUserDetails(clientId, true),
        this.appFacade.query.redirectedToCore$,
      ])
        .pipe(
          take(1),
          concatMap(([userDetails, redirectedToCore]) => {
            this.deleteChangedCachedDataIfLoggedInWithAnOtherUser(
              userDetails?.accountId
            );

            if (!this.appStatusService.online) {
              return of(true);
            }

            this.userDetailsFacade.setTermsConditionsAccepted(
              coreUser.termsAndConditionsAccepted
            );

            if (redirectedToCore) {
              this.appFacade.setRedirectedToCore(false);
              if (userDetails && !coreUser.termsAndConditionsAccepted) {
                this.appFacade.setRedirectedToCore(true);
                this.moduleNavigationService.navigateToCore(null, false);
              }
              return of(false);
            } else if (userDetails && !coreUser.termsAndConditionsAccepted) {
              this.appFacade.setRedirectedToCore(true);
              this.moduleNavigationService.navigateToCore(null, false);
            }
            return of(true);
          }),
          tap(() => {
            resolve();
          })
        )
        .subscribe();
    });
  }

  async initializeApplicationInsights(): Promise<void> {
    const userEmail = this.authInitializerService.user?.idTokenClaims
      ?.email as string;
    // it is not possible to use routes inside APP_INITIALIZER,
    const applicationInsightsService = this.injector.get(
      ApplicationInsightsService
    );

    applicationInsightsService.setUserId(userEmail);
  }

  //#region Private methods

  private getQueryParamByName(name: string): string {
    const URL = new URLSearchParams(window.location.search);
    return URL.get(name);
  }

  private deleteChangedCachedDataIfLoggedInWithAnOtherUser(
    currentAccountId: string
  ): void {
    combineLatest([
      this.cacheService.anyDprDetailsOfflineChanges(),
      this.cacheService.anyDprDetailsOfflineChanges(currentAccountId),
    ])
      .pipe(
        untilDestroyed(this),
        take(1),
        tap(([anyChanges, anyChangesMadeByCurrentAccount]) => {
          if (anyChanges && !anyChangesMadeByCurrentAccount) {
            this.offlineFacade.clearDprDetails();
            this.offlineFacade.setDprDetailsOfflineChanges(null);
          }
        })
      )
      .subscribe();
  }

  private async extractClientIdFromURL(user: CoreUser): Promise<string> {
    // the clientId can come as a query string parameter when navigating to DPR from another app's module picker
    const clientIdFromURL = this.getQueryParamByName('clientId');
    const clientIdFromRoute = this.routingService.routePrefix;
    let clientId = String.isNullOrWhiteSpace(clientIdFromURL)
      ? clientIdFromRoute !== 'null'
        ? clientIdFromRoute
        : null
      : clientIdFromURL;

    if (clientId && this.clientIdBlacklist.some((x) => clientId.includes(x))) {
      clientId = null;
    }

    if (this.appStatusService.online && clientId !== null) {
      clientId = await this.getFirstValidClientId(clientId, user);
    }

    return clientId;
  }

  private async getFirstValidClientId(
    clientId: string,
    user: CoreUser
  ): Promise<string> {
    let account: CoreAccount;

    if (!user) {
      return clientId;
    }

    if (user.accounts[0].accountType === AccountType.SystemAdmin) {
      return clientId;
    }

    if (user.accounts.find((x) => x.client?.id === clientId)) {
      account = user.accounts.find((x) => x.client?.id === clientId);

      return account.client.id;
    }

    const lastSelectedClientId = await this.getLastSelectedClient();

    account =
      user.accounts.find((x) => x.client?.id === lastSelectedClientId) ??
      user.accounts[0];

    return account.client.id;
  }

  private async getLastSelectedClient(): Promise<string> {
    return firstValueFrom(this.userDetailsFacade.query.selectedClientId$);
  }

  private async getCoreUserAccount(): Promise<CoreUser> {
    if (this.appStatusService.online) {
      const headers = new HttpHeaders({
        skipClientId: 'true',
      });
      return await firstValueFrom(
        this.genericHttpClientService.get<CoreUser>({
          context: HttpSharedCoreContext.GetUser,
          headers,
        })
      );
    }

    return null;
  }

  //#endregion
}
